// LICENSE PROTECTED SOFTWARE
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Plus, Clock, CheckCircle2, AlertTriangle, Send, X, Trash2, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { stkAPI } from '../lib/api';
import { toast } from 'sonner';

const STATUS_COLORS = {
  bekliyor: 'bg-amber-100 text-amber-800',
  yuklendi: 'bg-green-100 text-green-800',
  sonu_gecti: 'bg-red-100 text-red-800',
  tamamlandi: 'bg-green-100 text-green-800',
  kismi_tamamlandi: 'bg-blue-100 text-blue-800',
};

export default function STKDocumentRequestsPage() {
  const [items, setItems] = useState([]);
  const [stkList, setStkList] = useState([]);
  const [stkTotal, setStkTotal] = useState(0);
  const [stkLoadingMore, setStkLoadingMore] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailSearch, setDetailSearch] = useState('');
  const [detailStatusFilter, setDetailStatusFilter] = useState('all');
  const [detailPage, setDetailPage] = useState(1);
  const DETAIL_PAGE_SIZE = 50;
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    title: '', description: '', deadline: '', document_types: [''],
    stk_application_ids: [], send_email: false,
  });

  const load = async () => {
    try {
      setLoading(true);
      const r = await stkAPI.listDocRequests();
      setItems(r.data.items || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Yuklenemedi'); }
    finally { setLoading(false); }
  };

  const PAGE_SIZE = 100;
  const [stkPage, setStkPage] = useState(1);

  const loadStkList = async (page = 1) => {
    if (stkLoadingMore) return;
    try {
      setStkLoadingMore(true);
      const skip = (page - 1) * PAGE_SIZE;
      const r = await stkAPI.getApplications({ status: 'onaylandi', skip, limit: PAGE_SIZE });
      const arr = r.data?.applications || r.data?.items || (Array.isArray(r.data) ? r.data : []);
      const total = r.data?.total ?? arr.length;
      setStkTotal(total);
      setStkList(arr);
      setStkPage(page);
    } catch (e) { /* noop */ }
    finally { setStkLoadingMore(false); }
  };

  const onStkScroll = () => { /* artik klasik sayfalama, scroll handler pasif */ };

  useEffect(() => { load(); loadStkList(1); }, []);

  const openDetail = async (id) => {
    setShowDetail(id);
    setDetailSearch('');
    setDetailStatusFilter('all');
    setDetailPage(1);
    try {
      const r = await stkAPI.getDocRequest(id);
      setDetailData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Detay yuklenemedi'); }
  };

  const submit = async (e) => {
    e.preventDefault();
    const docTypes = form.document_types.map(s => s.trim()).filter(Boolean);
    if (!docTypes.length) { toast.error('En az 1 belge turu girin'); return; }
    if (!form.stk_application_ids.length) { toast.error('En az 1 STK secin'); return; }
    try {
      await stkAPI.createDocRequest({
        title: form.title,
        description: form.description || null,
        deadline: form.deadline ? new Date(form.deadline).toISOString() : null,
        document_types: docTypes,
        stk_application_ids: form.stk_application_ids,
        send_email: form.send_email,
      });
      toast.success(`Talep olusturuldu (${form.stk_application_ids.length} STK x ${docTypes.length} belge)`);
      setShowForm(false);
      setForm({ title: '', description: '', deadline: '', document_types: [''], stk_application_ids: [], send_email: false });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Olusturulamadi'); }
  };

  const deleteReq = async (id) => {
    if (!window.confirm('Bu talep silinsin mi? (Tum item kayitlari da silinir)')) return;
    try {
      await stkAPI.deleteDocRequest(id);
      toast.success('Talep silindi');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Silinemedi'); }
  };

  return (
    <div className="space-y-6" data-testid="stk-doc-requests-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><FileText className="w-6 h-6 text-blue-600"/> STK Belge Talepleri</h1>
          <p className="text-sm text-slate-500 mt-1">Onaylanmis STK'lardan sonradan ek belge talep edin (secim donemi temsilci belgesi vb.)</p>
        </div>
        <Button onClick={() => setShowForm(true)} data-testid="new-doc-request-btn"><Plus className="w-4 h-4 mr-1"/> Yeni Talep</Button>
      </div>

      {loading ? <p className="text-sm text-slate-500">Yukleniyor...</p> :
       items.length === 0 ?
        <Card><CardContent className="p-12 text-center"><FileText className="w-12 h-12 text-slate-300 mx-auto mb-3"/><p className="text-slate-600">Henuz belge talebi yok.</p></CardContent></Card>
        :
        <div className="space-y-3">
          {items.map(i => (
            <Card key={i.id} data-testid={`doc-request-${i.id}`}>
              <CardContent className="p-5">
                <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
                  <div>
                    <h3 className="font-semibold">{i.title}</h3>
                    {i.description && <p className="text-sm text-slate-600 mt-1">{i.description}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={STATUS_COLORS[i.overall_status] || 'bg-slate-100'}>{i.overall_status}</Badge>
                    {i.email_sent && <Badge variant="outline" className="text-xs"><Send className="w-3 h-3 mr-1"/>E-posta</Badge>}
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs text-slate-600 mt-3">
                  <div><span className="font-medium">Toplam:</span> {i.total}</div>
                  <div className="text-green-700"><span className="font-medium">Yuklenen:</span> {i.uploaded}</div>
                  <div className="text-amber-700"><span className="font-medium">Bekliyor:</span> {i.pending}</div>
                  <div className="text-red-700"><span className="font-medium">Sonu Gecti:</span> {i.overdue}</div>
                  <div><span className="font-medium">Son Tarih:</span> {i.deadline ? new Date(i.deadline).toLocaleDateString('tr-TR') : '-'}</div>
                </div>
                <div className="flex gap-2 mt-3 pt-3 border-t">
                  <Button size="sm" variant="outline" onClick={() => openDetail(i.id)}>Detay</Button>
                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteReq(i.id)}><Trash2 className="w-3 h-3"/></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      }

      {showForm && (
        <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">Yeni Belge Talebi</h2>
                <button onClick={() => setShowForm(false)}><X className="w-5 h-5 text-slate-400"/></button>
              </div>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label>Talep Basligi *</Label>
                  <Input required value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="doc-req-title-input" placeholder="orn: 2026 Donem Temsilci Yetkilendirme"/>
                </div>
                <div>
                  <Label>Aciklama / Not</Label>
                  <Textarea rows={2} value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="doc-req-desc-input"/>
                </div>
                <div>
                  <Label>Son Yukleme Tarihi</Label>
                  <Input type="datetime-local" value={form.deadline} onChange={e => setForm({...form, deadline: e.target.value})} data-testid="doc-req-deadline-input"/>
                </div>
                <div>
                  <Label>Talep Edilen Belgeler *</Label>
                  {form.document_types.map((dt, idx) => (
                    <div key={idx} className="flex gap-2 mt-1">
                      <Input value={dt} onChange={e => {
                        const arr = [...form.document_types]; arr[idx] = e.target.value; setForm({...form, document_types: arr});
                      }} placeholder="orn: Temsilci Yetkilendirme Belgesi"/>
                      {form.document_types.length > 1 && <Button type="button" size="sm" variant="outline" onClick={() => {
                        setForm({...form, document_types: form.document_types.filter((_, i) => i !== idx)});
                      }}><X className="w-3 h-3"/></Button>}
                    </div>
                  ))}
                  <Button type="button" size="sm" variant="outline" className="mt-2" onClick={() => setForm({...form, document_types: [...form.document_types, '']})}>
                    <Plus className="w-3 h-3 mr-1"/> Belge Ekle
                  </Button>
                </div>
                <div>
                  <Label>Hedef STK'lar * ({form.stk_application_ids.length} secili / {stkTotal} toplam)</Label>
                  <div className="border rounded-lg max-h-64 overflow-y-auto p-2 mt-1">
                    <div className="flex items-center gap-2 mb-2 pb-2 border-b">
                      <Checkbox checked={form.stk_application_ids.length === stkTotal && stkTotal > 0}
                        onCheckedChange={async (c) => {
                          if (c) {
                            try {
                              const r = await stkAPI.getApplications({ status: 'onaylandi', limit: 10000 });
                              const all = r.data?.applications || [];
                              setForm({ ...form, stk_application_ids: all.map(s => s.id) });
                            } catch { /* noop */ }
                          } else {
                            setForm({ ...form, stk_application_ids: [] });
                          }
                        }}/>
                      <span className="text-sm font-medium">Tumunu Sec ({stkTotal})</span>
                    </div>
                    {stkLoadingMore ? (
                      <p className="text-xs text-slate-400 text-center py-4">Yukleniyor...</p>
                    ) : stkList.length === 0 ? (
                      <p className="text-xs text-slate-400 text-center py-4">Onaylanmis STK bulunamadi</p>
                    ) : stkList.map(s => (
                      <label key={s.id} className="flex items-center gap-2 py-1 cursor-pointer text-sm">
                        <Checkbox checked={form.stk_application_ids.includes(s.id)}
                          onCheckedChange={c => {
                            const set = new Set(form.stk_application_ids);
                            if (c) set.add(s.id); else set.delete(s.id);
                            setForm({...form, stk_application_ids: Array.from(set)});
                          }}/>
                        {s.kurum_adi}
                      </label>
                    ))}
                  </div>
                  {/* Sayfalama: 100/sayfa */}
                  {stkTotal > PAGE_SIZE && (
                    <div className="flex items-center justify-between mt-2 px-1">
                      <span className="text-xs text-slate-500">
                        {(stkPage - 1) * PAGE_SIZE + 1}-{Math.min(stkPage * PAGE_SIZE, stkTotal)} / {stkTotal}
                      </span>
                      <div className="flex items-center gap-2">
                        <Button type="button" size="sm" variant="outline"
                          disabled={stkPage === 1 || stkLoadingMore}
                          onClick={() => loadStkList(stkPage - 1)} data-testid="stk-prev-page">
                          Onceki
                        </Button>
                        <span className="text-xs">Sayfa {stkPage} / {Math.max(1, Math.ceil(stkTotal / PAGE_SIZE))}</span>
                        <Button type="button" size="sm" variant="outline"
                          disabled={stkPage >= Math.ceil(stkTotal / PAGE_SIZE) || stkLoadingMore}
                          onClick={() => loadStkList(stkPage + 1)} data-testid="stk-next-page">
                          Sonraki
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={form.send_email} onCheckedChange={c => setForm({...form, send_email: !!c})} data-testid="doc-req-email-checkbox"/>
                  <span className="text-sm">E-posta ile de bildir <span className="text-xs text-slate-500">(sadece STK rolundeki kullanicilara)</span></span>
                </label>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Iptal</Button>
                  <Button type="submit" data-testid="doc-req-submit-btn">Talep Olustur</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {showDetail && detailData && (() => {
        const allItems = detailData.items || [];
        const q = detailSearch.trim().toLowerCase();
        const filtered = allItems.filter(it => {
          if (detailStatusFilter !== 'all' && it.effective_status !== detailStatusFilter) return false;
          if (!q) return true;
          return (it.stk_name || '').toLowerCase().includes(q)
              || (it.stk_sicil_no || '').toLowerCase().includes(q)
              || (it.document_type || '').toLowerCase().includes(q);
        });
        const totalPages = Math.max(1, Math.ceil(filtered.length / DETAIL_PAGE_SIZE));
        const safePage = Math.min(detailPage, totalPages);
        const pageItems = filtered.slice((safePage - 1) * DETAIL_PAGE_SIZE, safePage * DETAIL_PAGE_SIZE);
        const statusCounts = allItems.reduce((acc, it) => {
          acc[it.effective_status] = (acc[it.effective_status] || 0) + 1;
          return acc;
        }, {});
        return (
        <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-lg font-semibold">{detailData.item.title}</h2>
                  <p className="text-xs text-slate-500">
                    Son tarih: {detailData.item.deadline ? new Date(detailData.item.deadline).toLocaleString('tr-TR') : 'Belirsiz'}
                    {' · '}Toplam {allItems.length} kayit
                  </p>
                </div>
                <button onClick={() => { setShowDetail(null); setDetailData(null); }}><X className="w-5 h-5 text-slate-400"/></button>
              </div>

              {/* Filtreler */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4 pb-4 border-b">
                <div className="md:col-span-2">
                  <Input
                    placeholder="STK adi, sicil no veya belge turu ile arayin..."
                    value={detailSearch}
                    onChange={e => { setDetailSearch(e.target.value); setDetailPage(1); }}
                    data-testid="detail-search-input"
                  />
                </div>
                <select
                  className="border rounded-md px-3 py-2 text-sm bg-white"
                  value={detailStatusFilter}
                  onChange={e => { setDetailStatusFilter(e.target.value); setDetailPage(1); }}
                  data-testid="detail-status-filter"
                >
                  <option value="all">Tum Durumlar ({allItems.length})</option>
                  <option value="bekliyor">Bekliyor ({statusCounts.bekliyor || 0})</option>
                  <option value="yuklendi">Yuklendi ({statusCounts.yuklendi || 0})</option>
                  <option value="sonu_gecti">Suresi Gecti ({statusCounts.sonu_gecti || 0})</option>
                </select>
              </div>

              {/* Liste */}
              <div className="space-y-2">
                {pageItems.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-8">Filtreye uyan kayit yok.</p>
                ) : pageItems.map(it => (
                  <div key={it.id} className="border rounded-lg p-3 flex items-center justify-between gap-3" data-testid={`detail-item-${it.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{it.stk_name}</p>
                      <p className="text-xs text-slate-600 truncate">
                        {it.stk_sicil_no && <span className="text-slate-400">#{it.stk_sicil_no} · </span>}
                        {it.document_type}
                      </p>
                    </div>
                    <Badge className={STATUS_COLORS[it.effective_status]}>{it.effective_status}</Badge>
                    {it.uploaded_file_id && (
                      <a href={stkAPI.docRequestDownloadUrl(it.id)} target="_blank" rel="noreferrer"
                         className="inline-flex items-center text-xs text-blue-600 hover:underline">
                        <Download className="w-3 h-3 mr-1"/>Indir
                      </a>
                    )}
                  </div>
                ))}
              </div>

              {/* Sayfalama */}
              {filtered.length > DETAIL_PAGE_SIZE && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <p className="text-xs text-slate-500">
                    {(safePage - 1) * DETAIL_PAGE_SIZE + 1}-{Math.min(safePage * DETAIL_PAGE_SIZE, filtered.length)} / {filtered.length} kayit
                  </p>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" disabled={safePage === 1}
                      onClick={() => setDetailPage(safePage - 1)} data-testid="detail-prev-page">
                      Onceki
                    </Button>
                    <span className="text-sm">Sayfa {safePage} / {totalPages}</span>
                    <Button size="sm" variant="outline" disabled={safePage >= totalPages}
                      onClick={() => setDetailPage(safePage + 1)} data-testid="detail-next-page">
                      Sonraki
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        );
      })()}
    </div>
  );
}
